import axios from 'axios';
import { extractErrorMessage } from './errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // A failed SSO handoff says nothing about an existing session — wiping
      // storage here would strand a user who is already legitimately signed in
      // (and would make the recovery branch in Login.jsx unreachable).
      if ((error.config?.url || '').includes('/auth/oidc/complete')) {
        return Promise.reject(error);
      }
      // 2026-08-23: distinguish idle-expired from all other 401s so the login
      // page can surface a friendly "signed out for security" banner instead
      // of the generic "session expired" toast.
      const detail = error.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      const reason = code === 'session_idle_expired' ? 'idle'
                   : code === 'session_revoked' ? 'revoked'
                   : null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const target = reason ? `/login?reason=${reason}` : '/login';
      if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/emergency-access')) {
        window.location.href = target;
      }
    }
    // App-wide safety net (item 16): FastAPI 422 returns detail as an array of
    // objects. Any call site that passes err.response.data.detail straight to
    // toast/JSX would render raw objects (or crash React). Collapse it to a safe
    // human string here, once, so every screen is protected. Structured object
    // details ({message, review, code}) are left intact for pages that use them.
    const data = error.response?.data;
    if (data && Array.isArray(data.detail)) {
      data.detail = extractErrorMessage(error);
    }
    error.userMessage = extractErrorMessage(error);
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  heartbeat: () => api.post('/auth/heartbeat'),
  logout: () => api.post('/auth/logout'),
  sessionConfig: () => api.get('/auth/session-config'),
};

// User preferences (theme, and anything per-user we add later)
export const preferencesAPI = {
  get: () => api.get('/users/me/preferences'),
  update: (data) => api.patch('/users/me/preferences', data),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  updateRole: (userId, role) => api.patch(`/users/${userId}/role`, null, { params: { role } }),
  delete: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { new_password: newPassword }),
};

// Employees
export const employeesAPI = {
  list: (params) => api.get('/employees', { params }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data, force) => api.post('/employees', data, { params: force ? { force: true } : {} }),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  assetCardPdf: (id) => api.get(`/employees/${id}/asset-card-pdf`, { responseType: 'blob' }),
  // SSN — admin/HR only. Each reveal is audit-logged server-side.
  revealSsn: (id) => api.get(`/employees/${id}/ssn`),
  setSsn: (id, ssn) => api.put(`/employees/${id}/ssn`, { ssn }),
  clearSsn: (id) => api.delete(`/employees/${id}/ssn`),
  bulkAssignClients: (payload) => api.post('/employees/bulk-assign-clients', payload),
  bulkEdit: (payload) => api.post('/employees/bulk-edit', payload),
  companies: () => api.get('/employees/companies'),
  history: (id) => api.get(`/employees/${id}/history`),
};

// Employee duplicate merge (4.36.1, admin only)
export const employeeMergeAPI = {
  preview: (survivorId, loserId) => api.post('/employees/merge/preview', { survivor_id: survivorId, loser_id: loserId }),
  apply: (payload) => api.post('/employees/merge/apply', payload),
};

// Employee Licensing (multi-record per employee + expiration notifications)
export const employeeLicensesAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/licenses`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/licenses`, data),
  update: (employeeId, licenseId, data) => api.put(`/employees/${employeeId}/licenses/${licenseId}`, data),
  remove: (employeeId, licenseId) => api.delete(`/employees/${employeeId}/licenses/${licenseId}`),
  setPrimary: (employeeId, licenseId) => api.post(`/employees/${employeeId}/licenses/${licenseId}/set-primary`),
  upcoming: (days = 30) => api.get(`/license-expirations/upcoming`, { params: { days } }),
  // v4.33.3 — Firearms Qualification records + access-controlled credential files
  firearmsQualifications: (employeeId) => api.get(`/employees/${employeeId}/firearms-qualifications`),
  licenseCredentialFileUrl: (employeeId, licenseId) => `/employees/${employeeId}/license-credential-file/${licenseId}`,
  firearmsQualificationFileUrl: (employeeId, qualId) => `/employees/${employeeId}/firearms-qualification-file/${qualId}`,
};

// Employee Emergency Contacts (multi-record per employee)
export const employeeEmergencyContactsAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/emergency-contacts`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/emergency-contacts`, data),
  update: (employeeId, contactId, data) => api.put(`/employees/${employeeId}/emergency-contacts/${contactId}`, data),
  remove: (employeeId, contactId) => api.delete(`/employees/${employeeId}/emergency-contacts/${contactId}`),
  setPrimary: (employeeId, contactId) => api.post(`/employees/${employeeId}/emergency-contacts/${contactId}/set-primary`),
};

// Locations
export const locationsAPI = {
  list: (params) => api.get('/locations', { params }),
  get: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// ──────────────────────────────────────────────────────────────────────
// Lookup / Directory endpoints (2026-06-24)
// Low-sensitivity dropdown population — any authenticated user can call.
// Use these instead of locationsAPI.list/employeesAPI.list when the only
// thing the page needs is name+id for a dropdown. Full-record endpoints
// remain gated by require_cap("employees", "view").
// ──────────────────────────────────────────────────────────────────────
export const lookupsAPI = {
  locations: (params) => api.get('/lookups/locations', { params }),
  employees: (params) => api.get('/lookups/employees', { params }),
  vendors: (params) => api.get('/lookups/vendors', { params }),
  clients: (params) => api.get('/lookups/clients', { params }),
};

// Supervisory Access — reusable groups attached to client accounts.
export const supervisoryAPI = {
  levels: () => api.get('/supervisory-levels'),
  listGroups: (params) => api.get('/supervisory-groups', { params }),
  createGroup: (data) => api.post('/supervisory-groups', data),
  updateGroup: (id, data) => api.put(`/supervisory-groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/supervisory-groups/${id}`),
  getClientTeam: (clientId) => api.get(`/clients/${clientId}/supervisory-team`),
  saveClientTeam: (clientId, data) => api.put(`/clients/${clientId}/supervisory-team`, data),
};

// My Accounts — the caller's client accounts, their people, and evaluations due.
export const myAccountsAPI = {
  list: () => api.get('/my-accounts'),
  people: (clientId) => api.get(`/my-accounts/${clientId}/people`),
  coverage: () => api.get('/me/coverage'),
};

// ──────────────────────────────────────────────────────────────────────
// Phase 4 Sub-Pass 2 — Payroll APIs (effective-dated personnel records)
// All endpoints are gated by require_cap on their respective module + the
// HR payroll grant. Reveal endpoints additionally write an audit row.
// ──────────────────────────────────────────────────────────────────────
export const compensationAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/compensation`),
  current: (employeeId) => api.get(`/employees/${employeeId}/compensation/current`),
  asOf: (employeeId, date) => api.get(`/employees/${employeeId}/compensation/as-of`, { params: { date } }),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/compensation`, data),
  delete: (employeeId, recordId) => api.delete(`/employees/${employeeId}/compensation/${recordId}`),
  reveal: (employeeId, recordId) => api.get(`/employees/${employeeId}/compensation/${recordId}/reveal`),
};

export const federalW4API = {
  list: (employeeId) => api.get(`/employees/${employeeId}/federal-w4`),
  current: (employeeId) => api.get(`/employees/${employeeId}/federal-w4/current`),
  asOf: (employeeId, date) => api.get(`/employees/${employeeId}/federal-w4/as-of`, { params: { date } }),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/federal-w4`, data),
  delete: (employeeId, recordId) => api.delete(`/employees/${employeeId}/federal-w4/${recordId}`),
};

export const stateWithholdingAPI = {
  list: (employeeId, state) => api.get(`/employees/${employeeId}/state-withholding`, { params: state ? { state } : {} }),
  current: (employeeId, state) => api.get(`/employees/${employeeId}/state-withholding/current`, { params: { state } }),
  asOf: (employeeId, state, date) => api.get(`/employees/${employeeId}/state-withholding/as-of`, { params: { state, date } }),
  formConfig: (state) => api.get(`/employees/state-withholding/form-config/${state}`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/state-withholding`, data),
  delete: (employeeId, recordId) => api.delete(`/employees/${employeeId}/state-withholding/${recordId}`),
};

export const bankingAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/banking-accounts`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/banking-accounts`, data),
  update: (employeeId, accountId, data) => api.put(`/employees/${employeeId}/banking-accounts/${accountId}`, data),
  delete: (employeeId, accountId) => api.delete(`/employees/${employeeId}/banking-accounts/${accountId}`),
  reveal: (employeeId, accountId) => api.get(`/employees/${employeeId}/banking-accounts/${accountId}/reveal`),
};

// Items
export const itemsAPI = {
  // v4.38.0 item 2: /items now returns { items, total, page, page_size }.
  list: (params) => api.get('/items', { params }),
  // Page through ALL matching items (no silent cap). Returns { data, total }.
  listAll: async (params = {}) => {
    const pageSize = 200;
    let page = 1;
    let all = [];
    let total = 0;
    // Guard against runaway loops; 10k items is far beyond any real catalog.
    for (let i = 0; i < 100; i++) {
      const res = await api.get('/items', { params: { ...params, page, page_size: pageSize } });
      const batch = res.data?.items || [];
      total = res.data?.total ?? batch.length;
      all = all.concat(batch);
      if (all.length >= total || batch.length === 0) break;
      page += 1;
    }
    return { data: all, total };
  },
  get: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  adjustVariant: (itemId, variantSku, quantityChange, locationId) => api.post(`/items/${itemId}/variants/${encodeURIComponent(variantSku)}/adjust`, { quantity_change: quantityChange, location_id: locationId }),
  addVariantStock: (itemId, variantSku, locationId, quantity) => api.post(`/items/${itemId}/variants/${encodeURIComponent(variantSku)}/add-stock`, { location_id: locationId, quantity }),
  getItemInventory: (itemId) => api.get(`/items/${itemId}/inventory`),
  uploadImage: (itemId, formData) => api.post(`/items/${itemId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  listImages: (itemId) => api.get(`/items/${itemId}/images`),
  updateImage: (itemId, imageId, data) => api.patch(`/items/${itemId}/images/${imageId}`, null, { params: data }),
  deleteImage: (itemId, imageId) => api.delete(`/items/${itemId}/images/${imageId}`),
};

// Inventory
export const inventoryAPI = {
  list: (params) => api.get('/inventory', { params }),
  create: (data) => api.post('/inventory', data),
  adjust: (id, data) => api.post(`/inventory/${id}/adjust`, data),
};

// Requests
export const requestsAPI = {
  list: (params) => api.get('/requests', { params }),
  get: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post('/requests', data),
  approve: (id) => api.post(`/requests/${id}/approve`),
  reject: (id, reason) => api.post(`/requests/${id}/reject`, null, { params: { reason } }),
  startPicking: (id) => api.post(`/requests/${id}/start-picking`),
  markReady: (id) => api.post(`/requests/${id}/ready`),
  issue: (id) => api.post(`/requests/${id}/issue`),
  issueWithSignature: (id, data) => api.post(`/requests/${id}/issue-with-signature`, data),
  fulfill: (id) => api.post(`/requests/${id}/fulfill`),
  pickListPdf: (id) => api.get(`/requests/${id}/pick-list-pdf`, { responseType: 'blob' }),
  issuanceFormPdf: (id) => api.get(`/requests/${id}/issuance-form-pdf`, { responseType: 'blob' }),
};

// Vendors
export const vendorsAPI = {
  list: (params) => api.get('/vendors', { params }),
  get: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  importPreview: (formData) => api.post('/vendors/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  importConfirm: (previewId) => api.post('/vendors/import/confirm', { preview_id: previewId }),
  // Attachments (files uploaded against a vendor — contracts, W-9s, insurance)
  listAttachments: (vendorId) => api.get(`/vendors/${vendorId}/attachments`),
  uploadAttachment: (vendorId, formData) => api.post(
    `/vendors/${vendorId}/attachments/upload`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  downloadAttachmentUrl: (attId) => `${API_URL}/api/vendors/attachments/${attId}/download`,
  deleteAttachment: (attId) => api.delete(`/vendors/attachments/${attId}`),
};

// Clients (People & Places — parent records Jobs hang off later)
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  importPreview: (formData) => api.post('/clients/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  importConfirm: (payload) => api.post('/clients/import/confirm', payload),
  usage: (id) => api.get(`/clients/${id}/usage`),
  setActive: (id, isActive) => api.patch(`/clients/${id}/active`, null, { params: { is_active: isActive } }),
};

// Billing Items (Finance — canonical catalog for future job rate lines)
export const billingItemsAPI = {
  list: (params) => api.get('/billing-items', { params }),
  create: (data) => api.post('/billing-items', data),
  update: (id, data) => api.put(`/billing-items/${id}`, data),
  setActive: (id, isActive) => api.patch(`/billing-items/${id}/active`, null, { params: { is_active: isActive } }),
  usage: (id) => api.get(`/billing-items/${id}/usage`),
  delete: (id) => api.delete(`/billing-items/${id}`),
};

// Jobs — client jobs (billing unit). Rate lines are children; managed
// dropdowns (billing_frequency / uom / hrs_desc / type / equipment_other)
// live under /api/job-lookups.
export const jobsAPI = {
  list: (params) => api.get('/jobs', { params }),
  meta: () => api.get('/jobs/meta'),
  get: (id) => api.get(`/jobs/${id}`),
  resolveByNumber: (jobNumber) => api.get(`/jobs/resolve/by-number/${encodeURIComponent(jobNumber)}`),
  schedulingReadiness: (id) => api.get(`/jobs/${id}/scheduling-readiness`),
  securityLicenseCheck: (id) => api.get(`/jobs/${id}/security-license-check`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  setActive: (id, isActive) => api.patch(`/jobs/${id}/active`, null, { params: { is_active: isActive } }),
  delete: (id) => api.delete(`/jobs/${id}`),
  listRateLines: (jobId) => api.get(`/jobs/${jobId}/rate-lines`),
  addRateLine: (jobId, data) => api.post(`/jobs/${jobId}/rate-lines`, data),
  updateRateLine: (lineId, data) => api.put(`/rate-lines/${lineId}`, data),
  deleteRateLine: (lineId) => api.delete(`/rate-lines/${lineId}`),
  addLookup: (kind, value) => api.post(`/job-lookups/${kind}`, { value }),
  importPreview: (formData) => api.post('/jobs/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  importConfirm: (payload) => api.post('/jobs/import/confirm', payload),
  // Effective-dated rate card (Phase 1A). A rate CHANGE never edits in place.
  rateHistory: (jobId) => api.get(`/jobs/${jobId}/rate-history`),
  changeRateLine: (lineId, data) => api.post(`/rate-lines/${lineId}/change`, data),
};

// Sites — the physical place between a client and its jobs (Phase 1A).
export const sitesAPI = {
  list: (params) => api.get('/sites', { params }),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  setActive: (id, isActive) => api.patch(`/sites/${id}/active`, null, {
    params: { is_active: isActive },
  }),
  backfillPreview: (clientId) => api.post('/sites/backfill/preview', { client_id: clientId || null }),
  backfillConfirm: (payload) => api.post('/sites/backfill/confirm', payload),
};

// Job posts — the hinge between operations and billing.
export const jobPostsAPI = {
  list: (jobId, params) => api.get(`/jobs/${jobId}/posts`, { params }),
  create: (jobId, data) => api.post(`/jobs/${jobId}/posts`, data),
  update: (postId, data) => api.put(`/job-posts/${postId}`, data),
  setActive: (postId, isActive) => api.patch(`/job-posts/${postId}/active`, null, {
    params: { is_active: isActive },
  }),
  proposals: (jobId) => api.get(`/jobs/${jobId}/posts/proposals`),
  seed: (jobId, rows) => api.post(`/jobs/${jobId}/posts/seed`, { rows }),
};

// Scheduling — jobs and sites through the field-allowlisted read module, plus
// shift templates and schedule weeks. Every route here is capability-only
// (`scheduling:*`); none of them returns a rate, and there is no submit or
// approve endpoint in Phase B.
export const schedulingAPI = {
  jobs: (params) => api.get('/scheduling/jobs', { params }),
  jobDetail: (jobId) => api.get(`/scheduling/jobs/${jobId}`),
  listTemplates: (jobId, params) => api.get(`/jobs/${jobId}/shift-templates`, { params }),
  createTemplate: (jobId, data) => api.post(`/jobs/${jobId}/shift-templates`, data),
  updateTemplate: (id, data) => api.put(`/shift-templates/${id}`, data),
  setTemplateActive: (id, isActive) => api.patch(`/shift-templates/${id}/active`, null, {
    params: { is_active: isActive },
  }),
  week: (params) => api.get('/schedule-weeks', { params }),
  weekById: (weekId) => api.get(`/schedule-weeks/${weekId}`),
  jobWeeks: (jobId) => api.get(`/jobs/${jobId}/schedule-weeks`),
  generateWeek: (data) => api.post('/schedule-weeks/generate', data),
  // The grid's autosave. An array of cell changes, applied as a batch, with
  // one cross-job overtime recompute per affected officer-week rather than one
  // per cell. Partially applied on purpose: whatever needs a question comes
  // back in `blocked` and everything else is already saved.
  bulkUpsert: (weekId, body) => api.patch(`/schedule-weeks/${weekId}/shift-days`, body),
  eligibleEmployees: (params) => api.get('/scheduling-eligible-employees', { params }),
  addRegular: (jobId, data) => api.post(`/jobs/${jobId}/regulars`, data),
  removeRegular: (jobId, employeeId) => api.delete(`/jobs/${jobId}/regulars/${employeeId}`),
  getSettings: () => api.get('/scheduling-settings'),
  updateSettings: (data) => api.put('/scheduling-settings', data),
  setAssignment: (shiftDayId, data) => api.patch(`/shift-days/${shiftDayId}/assignment`, data),
  availableHours: (params) => api.get('/scheduling/available-hours', { params }),
  openPosts: (params) => api.get('/scheduling/open-posts', { params }),
  officerWeek: (params) => api.get('/scheduling/officer-week', { params }),
  callOffReasons: () => api.get('/scheduling/call-off-reasons'),
  // Holiday suggestion: accepted or declined by a person, never switched
  // silently. A declined suggestion stays declined through regeneration.
  setHoliday: (shiftDayId, action) =>
    api.patch(`/shift-days/${shiftDayId}/holiday`, { action }),
  // ── Phase C1: submit / approve / reject / unlock + reports ──────────────
  preSubmit: (weekId) => api.get(`/schedule-weeks/${weekId}/pre-submit`),
  weekState: (weekId) => api.get(`/schedule-weeks/${weekId}/state`),
  submitWeek: (weekId, payRateReasons = {}) =>
    api.post(`/schedule-weeks/${weekId}/submit`, { pay_rate_reasons: payRateReasons }),
  approveWeek: (weekId) =>
    api.post(`/schedule-weeks/${weekId}/approve`, {}),
  rejectWeek: (weekId, reason) => api.post(`/schedule-weeks/${weekId}/reject`, { reason }),
  unlockWeek: (weekId, reason) => api.post(`/schedule-weeks/${weekId}/unlock`, { reason }),
  selfApprovedReport: (params) =>
    api.get('/schedule-weeks/reports/self-approved', { params }),
  payRateQueue: (params) => api.get('/pay-rate-review-queue', { params }),
  markRateReviewed: (itemId) => api.post(`/pay-rate-review-queue/${itemId}/reviewed`),
  approvedHours: (params) => api.get('/approved-hours', { params }),
  // ── Phase C2: corrections after hours are used ──────────────────────────
  correctionImpact: (weekId) => api.get(`/schedule-weeks/${weekId}/correction-impact`),
  listCorrections: (weekId) => api.get(`/schedule-weeks/${weekId}/corrections`),
  openCorrection: (weekId, payload) =>
    api.post(`/schedule-weeks/${weekId}/corrections`, payload),
  confirmCorrection: (correctionId, reason) =>
    api.post(`/corrections/${correctionId}/confirm`, { reason }),
  cancelCorrection: (correctionId, reason) =>
    api.post(`/corrections/${correctionId}/cancel`, { reason }),
};

// ── Weekly timesheet (parallel test — 4.34.0 download) ────────────────────
// Download/upload of the standard weekly sheet. Hours captured here are for the
// November payroll parallel test only and never drive pay (isolated collections).
export const timesheetsAPI = {
  jobs: (weekStartDate) => api.get('/timesheets/jobs', { params: { week_start_date: weekStartDate } }),
  download: (jobId, weekStartDate) =>
    api.get('/timesheets/download', {
      params: { job_id: jobId, week_start_date: weekStartDate },
      responseType: 'blob',
    }),
  upload: (jobId, weekStartDate, spreadsheet, signedSheet) => {
    const fd = new FormData();
    fd.append('job_id', jobId);
    fd.append('week_start_date', weekStartDate);
    fd.append('spreadsheet', spreadsheet);
    if (signedSheet) fd.append('signed_sheet', signedSheet);
    return api.post('/timesheets/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  review: (jobId, weekStartDate) =>
    api.get('/timesheets/review', { params: { job_id: jobId, week_start_date: weekStartDate } }),
  submit: (jobId, weekStartDate, versionId, excludes) =>
    api.post('/timesheets/submit', { job_id: jobId, week_start_date: weekStartDate, version_id: versionId, excludes }),
  state: (jobId, weekStartDate) =>
    api.get('/timesheets/state', { params: { job_id: jobId, week_start_date: weekStartDate } }),
  approve: (jobId, weekStartDate, versionId) =>
    api.post('/timesheets/approve', {
      job_id: jobId, week_start_date: weekStartDate, version_id: versionId,
    }),
  reject: (jobId, weekStartDate, versionId, reason) =>
    api.post('/timesheets/reject', { job_id: jobId, week_start_date: weekStartDate, version_id: versionId, reason }),
};

// ── General Ledger (GL Phase 1) ───────────────────────────────────────────
export const glAPI = {
  listAccounts: (params) => api.get('/gl/accounts', { params }),
  createAccount: (payload) => api.post('/gl/accounts', payload),
  updateAccount: (number, patch) => api.patch(`/gl/accounts/${number}`, patch),
  settings: () => api.get('/gl/settings'),
  setFiscalStart: (month, confirmed) =>
    api.put('/gl/settings/fiscal-start-month', { month, confirmed }),
  trialBalance: (params) => api.get('/gl/trial-balance', { params }),
  accountDetail: (number, params) => api.get(`/gl/accounts/${number}/detail`, { params }),
  listEntries: (params) => api.get('/gl/entries', { params }),
  postEntry: (payload) => api.post('/gl/entries', payload),
  periods: (params) => api.get('/gl/periods', { params }),
  chartAnalyze: (formData) =>
    api.post('/gl/chart-import/analyze', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  chartCommit: (rows) => api.post('/gl/chart-import/commit', { rows }),
  openingAnalyze: (lines) => api.post('/gl/opening-balance/analyze', { lines }),
  openingCommit: (payload) => api.post('/gl/opening-balance/commit', payload),
};

// Email delivery — Resend webhook events, surfaced on /admin/email-delivery.
export const emailDeliveryAPI = {
  list: (params) => api.get('/email-delivery', { params }),
  sendTest: (to) => api.post('/email-delivery/test', { to }),
};

// Read-only production diagnostics (see backend/diagnostics.py).
export const diagnosticsAPI = {
  dataInventory: () => api.get('/admin/diagnostics/data-inventory'),
};

// Read-only environment state: version, preview vs production, migrations,
// money-conversion coverage, transaction capability.
export const systemStatusAPI = {
  get: () => api.get('/admin/system-status'),
  moneyDryRun: () => api.get('/admin/system-status/money-dry-run'),
  moneyGaps: () => api.get('/admin/system-status/money-gaps'),
  sensitiveRoleAccess: () => api.get('/admin/system-status/sensitive-role-access'),
  rateVisibility: () => api.get('/admin/system-status/rate-visibility'),
  schedulingGridAccess: () => api.get('/admin/system-status/scheduling-grid-access'),
  malformedDocuments: () => api.get('/admin/system-status/malformed-documents'),
  welcomeEmailsGet: () => api.get('/admin/welcome-emails'),
  welcomeEmailsSet: (enabled) => api.put('/admin/welcome-emails', { enabled }),
  credentialFileNamesBackfill: (dryRun) =>
    api.post(`/system/credential-file-names/backfill?dry_run=${dryRun ? 'true' : 'false'}`),
  moneyIntegerBackfill: (dryRun) =>
    api.post(`/system/money-integer/backfill?dry_run=${dryRun ? 'true' : 'false'}`),
  // Rule a superseded-W-4 / state-withholding money disagreement 'reviewed, superseded'.
  // Reporting-only: never touches a stored money value. Requires a note.
  moneyDisagreementRule: ({ collection, doc_id, field, note }) =>
    api.post('/system/money-disagreement-ruling', { collection, doc_id, field, note }),
  moneyDisagreementRulings: () => api.get('/system/money-disagreement-rulings'),
};



// Who receives the emails nobody triggered — low stock, licence expiry, bounces.
export const alertRecipientsAPI = {
  list: () => api.get('/alert-recipients'),
  update: (key, data) => api.put(`/alert-recipients/${key}`, data),
};

export const hoursTypesAPI = {
  list: (includeInactive = false) => api.get('/hours-types', {
    params: { include_inactive: includeInactive },
  }),
  create: (data) => api.post('/hours-types', data),
  update: (id, data) => api.put(`/hours-types/${id}`, data),
  setActive: (id, isActive) => api.patch(`/hours-types/${id}/active`, null, {
    params: { is_active: isActive },
  }),
};

// Employee ↔ job assignment. Ordering in the picker, not eligibility.
export const assignmentsAPI = {
  list: (params) => api.get('/employee-job-assignments', { params }),
  create: (data) => api.post('/employee-job-assignments', data),
  acceptClientSuggestion: (id) => api.post(`/employee-job-assignments/${id}/add-client`),
  setPrimary: (id, isPrimary) => api.patch(`/employee-job-assignments/${id}/primary`, null, {
    params: { is_primary: isPrimary },
  }),
  unassign: (id) => api.delete(`/employee-job-assignments/${id}`),
};

// Vendor Types (lookup — admin-managed)
export const vendorTypesAPI = {
  list: (params) => api.get('/vendor-types', { params }),
  create: (data) => api.post('/vendor-types', data),
  rename: (id, data) => api.put(`/vendor-types/${id}`, data),
  setActive: (id, isActive) => api.patch(`/vendor-types/${id}/active`, null, {
    params: { is_active: isActive },
  }),
  usage: (id) => api.get(`/vendor-types/${id}/usage`),
  delete: (id) => api.delete(`/vendor-types/${id}`),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  submit: (id) => api.post(`/purchase-orders/${id}/submit`),
  approve: (id) => api.post(`/purchase-orders/${id}/approve`),
  send: (id) => api.post(`/purchase-orders/${id}/send`),
  resendEmail: (id) => api.post(`/purchase-orders/${id}/resend-email`),
  receive: (id, items) => api.post(`/purchase-orders/${id}/receive`, items),
};

// Employee Wallet
export const walletAPI = {
  get: (employeeId) => api.get(`/employees/${employeeId}/wallet`),
};

// Returns
export const returnsAPI = {
  create: (data) => api.post('/returns', data),
};

// Notifications
export const notificationsAPI = {
  list: (unreadOnly) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
};

export const workOrdersAPI = {
  meta: () => api.get('/work-orders/meta'),
  list: (params) => api.get('/work-orders', { params }),
  get: (id) => api.get(`/work-orders/${id}`),
  submit: (data) => api.post('/work-orders', data),
  resubmit: (id, data) => api.put(`/work-orders/${id}`, data),
  addRateLine: (id, billingItemId, billRate) =>
    api.post(`/work-orders/${id}/rate-lines`,
      { billing_item_id: billingItemId, bill_rate: billRate ?? null }),
  approve: (id, comments) => api.post(`/work-orders/${id}/approve`, { comments }),
  reject: (id, comments) => api.post(`/work-orders/${id}/reject`, { comments }),
  uploadAttachment: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/work-orders/${id}/attachments`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAttachment: (attId) => api.delete(`/work-orders/attachments/${attId}`),
};

// Company Licenses (Compliance) — v4.38.0
export const companyLicensesAPI = {
  list: (params) => api.get('/company-licenses', { params }),
  upcoming: (params) => api.get('/company-licenses/upcoming', { params }),
  get: (id) => api.get(`/company-licenses/${id}`),
  history: (id) => api.get(`/company-licenses/${id}/history`),
  create: (data) => api.post('/company-licenses', data),
  update: (id, data) => api.put(`/company-licenses/${id}`, data),
  delete: (id) => api.delete(`/company-licenses/${id}`),
  export: () => api.get('/company-licenses/export', { responseType: 'blob' }),
  template: () => api.get('/company-licenses/import/template', { responseType: 'blob' }),
  importPreview: (formData) => api.post('/company-licenses/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  importConfirm: (payload) => api.post('/company-licenses/import/confirm', payload),
  listDocuments: (id) => api.get(`/company-licenses/${id}`).then(r => r.data.documents || []),
  uploadDocument: (id, formData) => api.post(`/company-licenses/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadDocument: (id, docId) => api.get(`/company-licenses/${id}/documents/${docId}/download`, { responseType: 'blob' }),
  deleteDocument: (id, docId) => api.delete(`/company-licenses/${id}/documents/${docId}`),
};

// Office Locations (People & Places) — v4.38.0
export const officesAPI = {
  list: (params) => api.get('/offices', { params }),
  get: (id) => api.get(`/offices/${id}`),
  history: (id) => api.get(`/offices/${id}/history`),
  links: (id) => api.get(`/offices/${id}/links`),
  create: (data) => api.post('/offices', data),
  update: (id, data) => api.put(`/offices/${id}`, data),
  deactivate: (id) => api.post(`/offices/${id}/deactivate`),
  remove: (id) => api.delete(`/offices/${id}`),
  assign: (id, payload) => api.post(`/offices/${id}/assign`, payload),
  export: () => api.get('/offices/export', { responseType: 'blob' }),
  template: () => api.get('/offices/import/template', { responseType: 'blob' }),
  importPreview: (formData) => api.post('/offices/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  importConfirm: (payload) => api.post('/offices/import/confirm', payload),
};

// Role-based dashboard (2026-06). One request fills every widget on the page.
export const dashboardAPI = {
  getWidgets: () => api.get('/dashboard/widgets'),
  getLayout: () => api.get('/dashboard/layout'),
  saveLayout: (widgets) => api.put('/dashboard/layout', { widgets }),
  resetLayout: () => api.post('/dashboard/layout/reset'),
  savePreferences: (prefs) => api.put('/dashboard/preferences', prefs),
  getData: (keys, refresh) =>
    api.get('/dashboard/data', { params: { widgets: keys.join(','), refresh: !!refresh } }),
  getRoleDefaults: () => api.get('/dashboard/role-defaults'),
  setRoleDefault: (role, widgets) => api.put(`/dashboard/role-defaults/${role}`, { widgets }),
  listLinks: (manage) => api.get('/dashboard/links', { params: { manage: !!manage } }),
  createLink: (data) => api.post('/dashboard/links', data),
  updateLink: (id, data) => api.put(`/dashboard/links/${id}`, data),
  deleteLink: (id) => api.delete(`/dashboard/links/${id}`),
};

export const announcementsAPI = {
  list: (limit) => api.get('/announcements', { params: { limit } }),
  manage: () => api.get('/announcements/manage'),
  audienceOptions: () => api.get('/announcements/audience-options'),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  remove: (id) => api.delete(`/announcements/${id}`),
  markRead: (id) => api.post(`/announcements/${id}/read`),
};

// Reports
export const reportsAPI = {
  inventorySummary: (params) => api.get('/reports/inventory-summary', { params }),
  employeeIssues: (params) => api.get('/reports/employee-issues', { params }),
  requestAging: () => api.get('/reports/request-aging'),
  downloadInventoryPdf: (params) => api.get('/reports/inventory-summary/pdf', { params, responseType: 'blob' }),
  downloadEmployeeIssuesPdf: (params) => api.get('/reports/employee-issues/pdf', { params, responseType: 'blob' }),
  downloadPurchaseOrdersPdf: () => api.get('/reports/purchase-orders/pdf', { responseType: 'blob' }),
  downloadRequestAgingPdf: () => api.get('/reports/request-aging/pdf', { responseType: 'blob' }),
  downloadItAssetsPdf: () => api.get('/reports/it-assets/pdf', { responseType: 'blob' }),
  downloadLowStockPdf: (params) => api.get('/reports/low-stock/pdf', { params, responseType: 'blob' }),
};

// Audit Logs
export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// AI Assistant
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  getChatHistory: (sessionId) => api.get('/ai/chat/history', { params: { session_id: sessionId } }),
  clearChatHistory: (sessionId) => api.delete('/ai/chat/history', { params: { session_id: sessionId } }),
  getForecast: () => api.get('/ai/forecast'),
  getForecastDashboard: () => api.get('/ai/forecast/dashboard'),
};

// Asset Check-in/Check-out
export const assetCheckoutAPI = {
  checkout: (data) => api.post('/assets/checkout', data),
  checkin: (checkoutId, data) => api.post(`/assets/checkin/${checkoutId}`, data),
  list: (params) => api.get('/assets/checkouts', { params }),
  getStatus: (itemId) => api.get(`/assets/checkouts/${itemId}/status`),
  getStatusByVariant: (itemId) => api.get(`/assets/checkouts/${itemId}/by-variant`),
  getHistoryByVariant: (itemId) => api.get(`/assets/checkouts/${itemId}/history`),
};

// Import
export const importAPI = {
  importItems: (formData) => api.post('/import/items', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadTemplate: (category) => api.get(`/import/template/${category}`, { responseType: 'blob' }),
};

// WinTeam HR imports (Admin only) — Employee Master (235 cols) and the NACHA
// bank transmission. Two-step: preview reports, confirm writes.
export const winteamImportAPI = {
  employeePreview: (formData) => api.post('/import/winteam/employees/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  employeeConfirm: (payload) => api.post('/import/winteam/employees/confirm', payload),
  employeeChangesCsv: (previewId) => api.get(`/import/winteam/employees/preview/${previewId}/changes.csv`, { responseType: 'blob' }),
  bankingPreview: (formData) => api.post('/import/winteam/banking/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bankingConfirm: (payload) => api.post('/import/winteam/banking/confirm', payload),
};

// v4.35.0 (PREVIEW) — WinTeam Scheduling Activity Report (SAR) parallel test.
// Read-only: upload parses, stores and compares; it never writes a schedule,
// timesheet, pay or invoice record.
export const sarAPI = {
  listImports: (includeSuperseded = true) => api.get('/sar/imports', { params: { include_superseded: includeSuperseded } }),
  upload: (formData) => api.post('/sar/imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getImport: (id) => api.get(`/sar/imports/${id}`),
  getRows: (id, skip = 0, limit = 100) => api.get(`/sar/imports/${id}/rows`, { params: { skip, limit } }),
  markFinal: (id, isFinal = true) => api.post(`/sar/imports/${id}/final`, null, { params: { is_final: isFinal } }),
  overtimeParity: (id) => api.get(`/sar/imports/${id}/overtime-parity`),
  hoursComparison: (id) => api.get(`/sar/imports/${id}/hours-comparison`),
};

// Payroll P1 gross pay (v4.36.0, PREVIEW parallel test). Read-only; the OT
// method (actual vs FLSA weighted average) is chosen per request.
export const payrollAPI = {
  grossPay: (id, otMethod) => api.get(`/payroll/gross-pay/${id}`, { params: { ot_method: otMethod } }),
  parity: (id, otMethod) => api.get(`/payroll/parity/${id}`, { params: { ot_method: otMethod } }),
  rateParity: (id, otMethod = 'actual') => api.get(`/payroll/rate-parity/${id}`, { params: { ot_method: otMethod } }),
  // Setup (v4.36.0)
  generatePeriods: (year, month, adjust, dryRun) => api.post('/payroll/pay-periods/generate', null, { params: { year, month, adjust_weekend_holiday: adjust, dry_run: dryRun } }),
  listPeriods: (payGroup) => api.get('/payroll/pay-periods', { params: payGroup ? { pay_group: payGroup } : {} }),
  payGroupReview: (fd) => api.post('/payroll/pay-groups/import/review', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  payGroupCommit: (fd) => api.post('/payroll/pay-groups/import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  payGroupSuggest: () => api.get('/payroll/pay-groups/suggest'),
  payGroupBulk: (rows) => api.post('/payroll/pay-groups/bulk', { rows }),
  payGroupUnassigned: () => api.get('/payroll/pay-groups/unassigned'),
  classReview: (fd) => api.post('/payroll/classifications/import/review', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  classCommit: (fd) => api.post('/payroll/classifications/import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  classMissing: () => api.get('/payroll/classifications/missing'),
  employeePayrollSetup: (employeeId) => api.get(`/payroll/employees/${employeeId}/payroll-setup`),
  assignments: (q) => api.get('/payroll/assignments', { params: q ? { q } : {} }),
  officerRateReview: (fd) => api.post('/payroll/officer-rates/import/review', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  officerRateCommit: (fd) => api.post('/payroll/officer-rates/import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  template: (kind) => {
    const url = kind === 'officer-rates' ? '/payroll/officer-rates/import/template'
      : kind === 'pay-groups' ? '/payroll/pay-groups/import/template'
        : '/payroll/classifications/import/template';
    return api.get(url, { responseType: 'blob' });
  },
  // Hours-type → earnings code map (v4.36.1, item 9)
  hoursTypeMap: () => api.get('/payroll/hours-type-map'),
  setHoursTypeMap: (hours_type_name, earnings_code, hours_type_code) => api.put('/payroll/hours-type-map', { hours_type_name, earnings_code, hours_type_code }),
  hoursTypeMapUnmapped: () => api.get('/payroll/hours-type-map/unmapped'),
  earningsCodes: () => api.get('/payroll/earnings-codes'),
  // Payroll P2 gross-to-net (v4.37.0)
  taxTables: (table_type) => api.get('/payroll/tax-tables', { params: table_type ? { table_type } : {} }),
  taxCalculator: (payload) => api.post('/payroll/tax-tables/calculator', payload),
  taxTableApprove: (id) => api.post(`/payroll/tax-tables/${id}/approve`),
  taxSetupIncomplete: () => api.get('/payroll/tax-setup/incomplete'),
  netParity: (runId, check_date) => api.get(`/payroll/net-parity/${runId}`, { params: { check_date } }),
  // Payroll P2 — full workspace wiring (v4.37.0)
  taxTableCreate: (payload) => api.post('/payroll/tax-tables', payload),
  taxTableDetail: (id) => api.get(`/payroll/tax-tables/${id}`),
  taxTemplate: (table_type) => api.get(`/payroll/tax-tables/template/${table_type}`, { responseType: 'blob' }),
  taxTemplateColumns: (table_type) => api.get(`/payroll/tax-tables/template/${table_type}`, { params: { fmt: 'json' } }),
  taxTableUpload: (payload) => api.post('/payroll/tax-tables/upload', payload),
  taxTableCopyForward: (payload) => api.post('/payroll/tax-tables/copy-forward', payload),
  taxTableNewVersion: (id) => api.post(`/payroll/tax-tables/${id}/new-version`),
  taxSetupAutofillPreview: () => api.get('/payroll/tax-setup/autofill-preview'),
  taxSetupAutofillApply: () => api.post('/payroll/tax-setup/autofill-apply'),
  deductionCodes: () => api.get('/payroll/deduction-codes'),
  employeeDirectory: () => api.get('/lookups/employees'),
  putDeductionCode: (payload) => api.put('/payroll/deduction-codes', payload),
  addElection: (payload) => api.post('/payroll/deduction-elections', payload),
  garnishments: (employeeId) => api.get(`/payroll/garnishments/${employeeId}`),
  addGarnishment: (payload) => api.post('/payroll/garnishments', payload),
  expensifyImport: (payload) => api.post('/payroll/reimbursements/expensify-import', payload),
  netRun: (payload) => api.post('/payroll/net-run', payload),
  wtImport: (payload) => api.post('/payroll/net-parity/import', payload),
  wtFinalize: (impId) => api.post(`/payroll/net-parity/import/${impId}/finalize`),
  netParityExport: (runId, check_date, fmt = 'csv') => api.get(`/payroll/net-parity/${runId}/export`, { params: { check_date, fmt }, responseType: 'blob' }),
  // Extra pay requests + approval (v4.36.1, item 11)
  extraPayList: (status) => api.get('/payroll/extra-pay', { params: status ? { status } : {} }),
  extraPayCreate: (payload) => api.post('/payroll/extra-pay', payload),
  extraPayApprove: (id) => api.post(`/payroll/extra-pay/${id}/approve`),
  extraPayReject: (id, reason) => api.post(`/payroll/extra-pay/${id}/reject`, { reason }),
  // Rate parity export with redaction (v4.36.1, item 10)
  rateParityExport: (id, fmt = 'csv', otMethod = 'actual') =>
    api.get(`/payroll/rate-parity/${id}/export`, { params: { fmt, ot_method: otMethod }, responseType: 'blob' }),
};

// Phase 5 — Onboarding Packet Import. Two-step: preview parses the PDF and
// returns the proposed employee + linked sub-records; confirm creates them.
export const onboardingAPI = {
  preview: (formData) => api.post('/onboarding/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirm: (payload) => api.post('/onboarding/confirm', payload),
};

export const exportAPI = {
  assets: (params) => api.get('/export/assets', { params, responseType: 'blob' }),
  checkouts: () => api.get('/export/checkouts', { responseType: 'blob' }),
  employees: () => api.get('/export/employees', { responseType: 'blob' }),
  locations: () => api.get('/export/locations', { responseType: 'blob' }),
};

// Evaluations
export const evaluationsAPI = {
  template: (postType, evaluationType = 'Annual', evaluationTrack = 'security_officer') =>
    api.get('/evaluations/template', { params: { post_type: postType, evaluation_type: evaluationType, evaluation_track: evaluationTrack } }),
  list: (params) => api.get('/evaluations', { params }),
  get: (id) => api.get(`/evaluations/${id}`),
  create: (data) => api.post('/evaluations', data),
  update: (id, data) => api.put(`/evaluations/${id}`, data),
  remove: (id) => api.delete(`/evaluations/${id}`),  // Admin-only hard delete
  submit: (id) => api.post(`/evaluations/${id}/submit`),
  sign: (id, data) => api.post(`/evaluations/${id}/sign`, data),
  decline: (id, data) => api.post(`/evaluations/${id}/decline`, data),
  returnToSupervisor: (id, data) => api.post(`/evaluations/${id}/return`, data),
  addResponse: (id, data) => api.post(`/evaluations/${id}/response`, data),
  pdf: (id) => api.get(`/evaluations/${id}/pdf`, { responseType: 'blob' }),
  regenerateEmployeeLink: (id) => api.post(`/evaluations/${id}/regenerate-employee-link`),
  stuckAtEmployee: () => api.get('/evaluations-stuck-at-employee'),
  mySignQueue: () => api.get('/evaluations-my-sign-queue'),
  allSignQueue: () => api.get('/evaluations-all-sign-queue'),
  completionOverview: () => api.get('/evaluations-completion-overview'),
  eligibleEmployees: () => api.get('/evaluations-eligible-employees'),
  evalSigners: (empId) => api.get(`/employees/${empId}/eval-signers`),
  whoCanSign: (id) => api.get(`/evaluations/${id}/who-can-sign`),
  liveEmployeeSnapshot: (id) => api.get(`/evaluations/${id}/live-employee-snapshot`),
  // 2026-08-22: track lookup + Part 2 (6-Month Re-Review) endpoints.
  getTracks: (employeeId) => api.get('/evaluations/tracks', { params: employeeId ? { employee_id: employeeId } : {} }),
  openPart2: (id) => api.post(`/evaluations/${id}/part2/open`),
  updatePart2: (id, data) => api.put(`/evaluations/${id}/part2`, data),
  signPart2: (id, data) => api.post(`/evaluations/${id}/part2/sign`, data),
  sendPart2EmployeeLink: (id) => api.post(`/evaluations/${id}/part2/send-employee-link`),
  signatureIntegrity: () => api.get('/evaluations/admin/signature-integrity'),
  reopenReviewer: (id, reason, part) => api.post(`/evaluations/${id}/reopen-reviewer`, { note: reason, part }),
};

// Public (no-auth) evaluation OTP & sign flow.
// These intentionally bypass the global axios interceptor's auth header.
export const publicEvaluationAPI = {
  requestOtp: (token, email) =>
    axios.post(`${API_URL}/api/public/evaluations/${token}/request-otp`, { email }),
  verifyOtp: (token, email, otp) =>
    axios.post(`${API_URL}/api/public/evaluations/${token}/verify-otp`, { email, otp }),
  view: (token, scopedToken) =>
    axios.get(`${API_URL}/api/public/evaluations/${token}/view`, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
  sign: (token, scopedToken, payload) =>
    axios.post(`${API_URL}/api/public/evaluations/${token}/sign`, payload, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
  uploadCredential: (token, scopedToken, itemKey, formData) =>
    axios.post(`${API_URL}/api/public/evaluations/${token}/credentials/${itemKey}/upload`, formData, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
  enterQualification: (token, scopedToken, itemKey, payload) =>
    axios.post(`${API_URL}/api/public/evaluations/${token}/credentials/${itemKey}/qualification`, payload, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
};

// A7: public "view / download my copy" portal for a completed evaluation.
export const publicEvaluationCopyAPI = {
  meta: (copyToken) =>
    axios.get(`${API_URL}/api/public/evaluations/copy/${copyToken}/meta`),
  requestOtp: (copyToken, email) =>
    axios.post(`${API_URL}/api/public/evaluations/copy/${copyToken}/request-otp`, { email }),
  verifyOtp: (copyToken, email, otp) =>
    axios.post(`${API_URL}/api/public/evaluations/copy/${copyToken}/verify-otp`, { email, otp }),
  pdfUrl: (copyToken) => `${API_URL}/api/public/evaluations/copy/${copyToken}/pdf`,
};

// Public credential return (reject → replace) flow. OTP-gated, 10-day link.
export const publicCredentialReturnAPI = {
  info: (token) =>
    axios.get(`${API_URL}/api/public/evaluations/return/${token}`),
  requestOtp: (token, email) =>
    axios.post(`${API_URL}/api/public/evaluations/return/${token}/request-otp`, { email }),
  verifyOtp: (token, email, otp) =>
    axios.post(`${API_URL}/api/public/evaluations/return/${token}/verify-otp`, { email, otp }),
  items: (token, scopedToken) =>
    axios.get(`${API_URL}/api/public/evaluations/return/${token}/items`, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
  uploadCredential: (token, scopedToken, itemKey, formData) =>
    axios.post(`${API_URL}/api/public/evaluations/return/${token}/credentials/${itemKey}/upload`, formData, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
  enterQualification: (token, scopedToken, itemKey, payload) =>
    axios.post(`${API_URL}/api/public/evaluations/return/${token}/credentials/${itemKey}/qualification`, payload, {
      headers: { Authorization: `Bearer ${scopedToken}` },
    }),
};

// Employee Attachments
export const employeeAttachmentsAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/attachments`),
  upload: (employeeId, formData) => api.post(`/employees/${employeeId}/attachments/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (attId) => api.get(`/employees/attachments/${attId}/download`, { responseType: 'blob' }),
  delete: (attId) => api.delete(`/employees/attachments/${attId}`),
};

// Employee Folders
export const employeeFoldersAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/folders`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/folders`, data),
  rename: (employeeId, folderId, name) => api.patch(`/employees/${employeeId}/folders/${folderId}`, { name }),
  delete: (employeeId, folderId) => api.delete(`/employees/${employeeId}/folders/${folderId}`),
};

// Supervisor Log
export const supervisorLogAPI = {
  list: (employeeId) => api.get(`/employees/${employeeId}/supervisor-log`),
  create: (employeeId, data) => api.post(`/employees/${employeeId}/supervisor-log`, data),
  update: (entryId, data) => api.put(`/employees/supervisor-log/${entryId}`, data),
  delete: (entryId) => api.delete(`/employees/supervisor-log/${entryId}`),
  uploadAttachment: (employeeId, formData) => api.post(`/employees/${employeeId}/supervisor-log/attachments/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportPdf: (employeeId) => api.get(`/employees/${employeeId}/supervisor-log/pdf`, { responseType: 'blob' }),
};

export default api;
